import {
  AcademicYearDto,
  CopyModuleStructureDto,
  IModuleManagementClient,
  ModuleCategoryDto,
  ModuleDto,
  ModuleExecutionDto,
} from '@mse-reg/clients';

import { createAppThunk, RootState } from '@mse-reg/app/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { addExceptionNotification, addSuccess } from '../../notification.slice';
import { moduleSelectionActions, moduleSelectionThunks } from '../../selections/module-selection.slice';

const refreshFiltersAsync = createAppThunk(
  'moduleManagement/REFRESH_FILTERS',
  async (_, deps, dispatch) => {
    try {
      return await dispatch(moduleSelectionThunks.fetchModuleFilter()).unwrap();
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while refreshing the module filter!`));
    }
    return null;
  },
  (r) => ({ moduleSelectionClient: r.resolve('IModuleSelectionClient') }),
);

const saveModuleCategoryAsync = createAppThunk(
  'moduleManagement/SAVE_MODULE_CATEGORY',
  async (
    moduleCategory: ModuleCategoryDto,
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
  ) => {
    try {
      const result = await moduleManagementClient.insertOrUpdateCategory(moduleCategory);
      await dispatch(moduleSelectionThunks.setFilterAsync({ category: result })).unwrap();
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Category '${moduleCategory.code}' was successfully updated.`));
      return result;
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while saving module category '${moduleCategory.code}'!`));
    }
    return null;
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const copyAcademicYearAsync = createAppThunk(
  'moduleManagement/COPY_ACADEMIC_YEAR',
  async (
    options: CopyModuleStructureDto,
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
    getState,
  ) => {
    try {
      const result = await moduleManagementClient.copyAcademicYear(options);
      await dispatch(refreshFiltersAsync());
      const academicYears = getState().semesterSelection.semesterFilter.academicYears;
      const from = academicYears.find((a) => a.id === options.fromAcademicYearId);
      const to = academicYears.find((a) => a.id === options.toAcademicYearId);
      dispatch(addSuccess(`Academic year was successfully copied from '${from.name}' to '${to.name}'.`));
      return result;
    } catch (err) {
      const academicYears = getState().semesterSelection.semesterFilter.academicYears;
      const from = academicYears.find((a) => a.id === options.fromAcademicYearId);
      const to = academicYears.find((a) => a.id === options.toAcademicYearId);
      dispatch(addExceptionNotification(err, `Error while copying academic year '${from.name}' to '${to.name}'!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const deleteModuleCategoryAsync = createAppThunk(
  'moduleManagement/DELETE_MODULE_CATEGORY',
  async (
    category: ModuleCategoryDto,
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
  ) => {
    try {
      await moduleManagementClient.deleteCategory(category.id);
      await dispatch(moduleSelectionThunks.setFilterAsync({}));
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Category '${category.code}' was successfully deleted.`));
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while deleting category '${category.code}'!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const updateModuleAsync = createAppThunk(
  'moduleManagement/UPDATE_MODULE',
  async (
    module: ModuleDto,
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
  ) => {
    try {
      const result = await moduleManagementClient.updateModule(module);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Module '${result.code}' was successfully updated.`));
      return result;
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while updating module '${module.code}'!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const insertModuleAsync = createAppThunk(
  'moduleManagement/INSERT_MODULE',
  async (
    module: ModuleDto,
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
  ) => {
    try {
      const result = await moduleManagementClient.insertModule(module);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Module '${result.code}' was successfully saved.`));
      await dispatch(moduleSelectionThunks.setFilterAsync({ module: result })).unwrap();
      return result;
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while inserting module!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const saveModuleExecutionsAsync = createAppThunk(
  'moduleManagement/SAVE_MODULE_EXECUTIONS',
  async (
    {
      executions,
      module,
      academicYear,
    }: {
      executions: ModuleExecutionDto[];
      module: ModuleDto;
      academicYear: AcademicYearDto;
    },
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
  ) => {
    try {
      const result = await moduleManagementClient.updateModuleExecutions(
        executions.map((e) => e),
        module.id,
        academicYear.id,
      );
      dispatch(moduleSelectionActions.resetExecutions());
      await dispatch(refreshFiltersAsync());
      await dispatch(moduleSelectionThunks.fetchModuleExecutionsForYearAsync(academicYear.id)).unwrap();
      dispatch(addSuccess(`Module executions of '${module.code}' were successfully saved.`));
      return result;
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while saving module executions of '${module.code}'!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const deleteModuleAsync = createAppThunk(
  'moduleManagement/DELETE_MODULE',
  async (
    module: ModuleDto,
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
  ) => {
    try {
      await moduleManagementClient.deleteModule(module?.id);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Module '${module.code}' was successfully deleted.`));
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while deleting module '${module.code}'!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const saveModuleExecutionAsync = createAppThunk(
  'moduleManagement/SAVE_MODULE_EXECUTION',
  async (
    {
      execution,
      module,
      academicYear,
    }: {
      execution: ModuleExecutionDto;
      module: ModuleDto;
      academicYear: AcademicYearDto;
    },
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
    getState,
  ) => {
    try {
      await moduleManagementClient.updateModuleExecutions([execution], module.id, academicYear.id);
      await dispatch(refreshFiltersAsync());
      const currentAcademicYearId = getState().semesterSelection.selectedAcademicYear.id;
      await dispatch(moduleSelectionThunks.fetchModuleExecutionsForYearAsync(currentAcademicYearId)).unwrap();
      dispatch(addSuccess(`Execution '${execution.code || module.code}' was successfully saved.`));
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while saving execution!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

const deleteModuleExecutionAsync = createAppThunk(
  'moduleManagement/DELETE_MODULE_EXECUTION',
  async (
    execution: ModuleExecutionDto,
    { moduleManagementClient }: { moduleManagementClient: IModuleManagementClient },
    dispatch,
    getState,
  ) => {
    try {
      await moduleManagementClient.deleteModuleExecution(execution.id);
      await dispatch(refreshFiltersAsync());
      dispatch(moduleSelectionActions.resetExecutions());
      const academicYearId = getState().semesterSelection.selectedAcademicYear.id;
      await dispatch(moduleSelectionThunks.fetchModuleExecutionsForYearAsync(academicYearId)).unwrap();
      dispatch(addSuccess(`Execution '${execution.code}' was successfully deleted.`));
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while deleting execution '${execution.code}'!`));
    }
  },
  (r) => ({ moduleManagementClient: r.resolve('IModuleManagementClient') }),
);

// State
const initialState = {
  loading: false,
  sidebarFilter: '',
  newModuleCategory: null as ModuleCategoryDto,
  newModule: null as ModuleDto,
  moduleLoading: null as number,
  moduleExecutionLoading: null as number,
  executions: [] as ModuleExecutionDto[],
};

export type ModuleManagementState = typeof initialState;

const slice = createSlice({
  name: 'moduleManagement',
  initialState,
  reducers: {
    addModuleCategory(state: ModuleManagementState) {
      const moduleCategory = { code: '', name: '', isActive: true, id: 0 } as ModuleCategoryDto;
      state.newModuleCategory = moduleCategory;
      state.newModule = null;
    },
    addModule(state: ModuleManagementState, action: PayloadAction<{ category: ModuleCategoryDto; id: number }>) {
      const module: ModuleDto = {
        code: '',
        name: '',
        isActive: true,
        id: action.payload.id,
        categoryId: action.payload.category.id,
        partialCode: '',
        hasRelatedData: false,
        recommendedMaxParticipants: 120,
      };
      state.newModule = module;
    },
    cancelAdding(state: ModuleManagementState) {
      state.newModule = null;
      state.newModuleCategory = null;
    },
    setSidebarFilter(state: ModuleManagementState, action: PayloadAction<string>) {
      state.sidebarFilter = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(saveModuleCategoryAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveModuleCategoryAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(saveModuleCategoryAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(copyAcademicYearAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(copyAcademicYearAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(copyAcademicYearAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteModuleCategoryAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteModuleCategoryAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteModuleCategoryAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateModuleAsync.pending, (state, action) => {
        state.moduleLoading = action.meta.arg.id;
      })
      .addCase(updateModuleAsync.rejected, (state) => {
        state.moduleLoading = null;
      })
      .addCase(updateModuleAsync.fulfilled, (state) => {
        state.moduleLoading = null;
      })
      .addCase(insertModuleAsync.pending, (state, action) => {
        state.moduleLoading = action.meta.arg.id;
      })
      .addCase(insertModuleAsync.rejected, (state) => {
        state.moduleLoading = null;
      })
      .addCase(insertModuleAsync.fulfilled, (state) => {
        state.moduleLoading = null;
      })
      .addCase(deleteModuleAsync.pending, (state, action) => {
        state.moduleLoading = action.meta.arg.id;
      })
      .addCase(deleteModuleAsync.rejected, (state) => {
        state.moduleLoading = null;
      })
      .addCase(deleteModuleAsync.fulfilled, (state) => {
        state.moduleLoading = null;
      })
      .addCase(saveModuleExecutionsAsync.pending, (state, action) => {
        state.moduleLoading = action.meta.arg.module.id;
      })
      .addCase(saveModuleExecutionsAsync.rejected, (state) => {
        state.moduleLoading = null;
      })
      .addCase(saveModuleExecutionsAsync.fulfilled, (state) => {
        state.moduleLoading = null;
      })
      .addCase(saveModuleExecutionAsync.pending, (state, action) => {
        state.moduleExecutionLoading = action.meta.arg.execution.id;
      })
      .addCase(saveModuleExecutionAsync.rejected, (state) => {
        state.moduleExecutionLoading = null;
      })
      .addCase(saveModuleExecutionAsync.fulfilled, (state) => {
        state.moduleExecutionLoading = null;
      })
      .addCase(deleteModuleExecutionAsync.pending, (state, action) => {
        state.moduleExecutionLoading = action.meta.arg.id;
      })
      .addCase(deleteModuleExecutionAsync.rejected, (state) => {
        state.moduleExecutionLoading = null;
      })
      .addCase(deleteModuleExecutionAsync.fulfilled, (state) => {
        state.moduleExecutionLoading = null;
      });
  },
});

const selectModuleCategories = (state: RootState) => state.moduleSelection.moduleCategories ?? [];
const selectModules = (state: RootState) => state.moduleSelection.modules ?? [];
const selectSidebarFilter = (state: RootState) => state.moduleManagement.sidebarFilter ?? '';

const getOptionalText = (item: unknown, keys: string[]) => {
  const data = item as Record<string, unknown>;
  return keys
    .map((key) => data[key])
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ');
};

const matchesFilter = (value: string, normalizedFilter: string) => {
  if (!normalizedFilter) {
    return true;
  }
  return value.toLowerCase().includes(normalizedFilter);
};

// Lower score = higher priority. 0: code starts with, 1: any field starts with, 2: contains
const matchScore = (value: string, code: string, normalizedFilter: string): number => {
  if (!normalizedFilter) return 0;
  if (code.toLowerCase().startsWith(normalizedFilter)) return 0;
  if (value.toLowerCase().startsWith(normalizedFilter)) return 1;
  return 2;
};

const categoryMatches = (category: ModuleCategoryDto, normalizedFilter: string) =>
  matchesFilter(
    [category.code, getOptionalText(category, ['name', 'title', 'description'])].filter(Boolean).join(' '),
    normalizedFilter,
  );

const categoryScore = (category: ModuleCategoryDto, normalizedFilter: string) =>
  matchScore(
    [category.code, getOptionalText(category, ['name', 'title', 'description'])].filter(Boolean).join(' '),
    category.code,
    normalizedFilter,
  );

const moduleMatches = (module: ModuleDto, normalizedFilter: string) =>
  matchesFilter(
    [module.code, getOptionalText(module, ['name', 'title', 'description'])].filter(Boolean).join(' '),
    normalizedFilter,
  );

const moduleScore = (module: ModuleDto, normalizedFilter: string) =>
  matchScore(
    [module.code, getOptionalText(module, ['name', 'title', 'description'])].filter(Boolean).join(' '),
    module.code,
    normalizedFilter,
  );

export const selectModuleSidebarData = createSelector(
  [selectModuleCategories, selectModules, selectSidebarFilter],
  (categories, modules, sidebarFilter) => {
    const normalizedFilter = sidebarFilter.trim().toLowerCase();
    const hasFilter = normalizedFilter.length > 0;

    const activeCategories = categories
      .filter(
        (category) =>
          category.isActive &&
          (!hasFilter ||
            categoryMatches(category, normalizedFilter) ||
            modules.some(
              (module) =>
                module.categoryId === category.id && module.isActive && moduleMatches(module, normalizedFilter),
            )),
      )
      .sort((a, b) => (hasFilter ? categoryScore(a, normalizedFilter) - categoryScore(b, normalizedFilter) : 0));

    const inactiveCategories = categories
      .filter(
        (category) =>
          !category.isActive || modules.some((module) => module.categoryId === category.id && !module.isActive),
      )
      .filter(
        (category) =>
          !hasFilter ||
          categoryMatches(category, normalizedFilter) ||
          modules.some(
            (module) =>
              module.categoryId === category.id && !module.isActive && moduleMatches(module, normalizedFilter),
          ),
      )
      .sort((a, b) => (hasFilter ? categoryScore(a, normalizedFilter) - categoryScore(b, normalizedFilter) : 0));

    const activeModulesByCategoryId = Object.fromEntries(
      activeCategories.map((category) => [
        category.id,
        modules
          .filter(
            (module) =>
              module.categoryId === category.id &&
              module.isActive &&
              (!hasFilter || moduleMatches(module, normalizedFilter)),
          )
          .sort((a, b) => (hasFilter ? moduleScore(a, normalizedFilter) - moduleScore(b, normalizedFilter) : 0)),
      ]),
    ) as Record<number, ModuleDto[]>;

    const inactiveModulesByCategoryId = Object.fromEntries(
      inactiveCategories.map((category) => [
        category.id,
        modules
          .filter(
            (module) =>
              module.categoryId === category.id &&
              !module.isActive &&
              (!hasFilter || moduleMatches(module, normalizedFilter)),
          )
          .sort((a, b) => (hasFilter ? moduleScore(a, normalizedFilter) - moduleScore(b, normalizedFilter) : 0)),
      ]),
    ) as Record<number, ModuleDto[]>;

    return {
      hasFilter,
      sidebarFilter,
      activeCategories,
      inactiveCategories,
      activeModulesByCategoryId,
      inactiveModulesByCategoryId,
    };
  },
);

export type ModuleManagementActions = typeof slice.actions;
export const moduleManagementReducer = slice.reducer;
export const moduleManagementActions = slice.actions;
export const moduleManagementThunks = {
  deleteModuleAsync,
  deleteModuleCategoryAsync,
  deleteModuleExecutionAsync,
  saveModuleCategoryAsync,
  saveModuleExecutionsAsync,
  saveModuleExecutionAsync,
  insertModuleAsync,
  updateModuleAsync,
  copyAcademicYearAsync,
};
